import React, { useState, Fragment, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Card,
  Table,
  Tooltip,
  Row,
  Button,
  Col,
  Checkbox,
  Modal,
  message,
  Menu,
  Dropdown,
  Space,
  Grid,
  List,
  Skeleton,
  Typography,
  Divider,
  Popover,
} from "antd";
import {
  ExclamationCircleOutlined,
  EditOutlined,
  DownOutlined,
  ColumnHeightOutlined,
  VerticalAlignMiddleOutlined,
  SettingOutlined,
  UpOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./deviceTable.module.scss";
import IoTModal from "./DeviceTableF_IoTModal";
// import { EditOutlined } from "react-icons/ri";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import type { CheckboxChangeEvent } from "antd/es/checkbox"; // ✅ 引入型別
import SettingResultModal from "../Setting/BulkConfigF_SettingResultModal";
import DeviceStateModal from "./DeviceTableF_StateModal";
import EditCityModal from "./Location_Modal";
import HealthModal from "./Health_Modal";
import AutoRefresh from "Components/Shared/AutoRefresh";
import TrackMap from "./DeviceTableF_TrackerModal";
import Refresh from "Components/Shared/Refresh";
import Filter from "./Filter";
import Icons from "./TableIcons";

// Util
import { CharacterVerify, NumberVerify, CoordinateVerify } from "../../../Utility/RegExp";
import { TableData } from "Utility/Types/status_identity_get_set";
import { TimestampToDate, ConnectionTime } from "Utility/Fetch";
// import { deviceNameToID } from "../Setting/utils";

// redux & query
import {
  setEditCityVisible,
  setResultList,
  setResultModalVisible,
  setImportSource,
  setEditDescriptionVisible,
} from "../store/topologySlice";
import { useAppSelector, useAppDispatch } from "store/hook";
import {
  useDeviceReboot,
  useDeleteNode,
  useDviceRename,
  useUpdateLocations,
  useEditCoordinate,
  useUpdateDescriptions,
} from "../queries";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { useUpdateStatus } from "Components/Dashboard/queries";
import { useCustomerInf } from "Components/Management/queries";
import { mixin } from "cypress/types/lodash";
import { i18n } from "i18next";
import EditDescriptionModal from "./Description_Modal";

const { Column } = Table;
const { confirm } = Modal;
const { useBreakpoint } = Grid;
const { Paragraph } = Typography;
const { Group: CheckboxGroup } = Checkbox;

const WarningString = "level: super_super/get not allowed";
// /overview 內 Device 的 頁面

export interface columnSetting {
  Location: boolean;
  Description: boolean;
  GPS: boolean;
  PhoneNumber: boolean;
  LastUpdate: boolean;
  LatestConnectionTime: boolean;
}

const statusOptions = ["Location", "Description", "GPS", "PhoneNumber", "LastUpdate", "LatestConnectionTime"];

interface checkRowProps {
  rowOptionKey: string;
  rowOptions: string[];
  selectedOptions: Record<string, boolean>;
  setSelectedOptions: (arg: Record<string, boolean>) => void;
  t: (key: string) => string;
}

export const CheckDeviceRow: React.FC<checkRowProps> = ({
  rowOptionKey,
  rowOptions,
  selectedOptions,
  setSelectedOptions,
  t,
}) => {
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(false);

  // 初始化 localStorage，確保所有 key 都存在
  useEffect(() => {
    const columnSetting = localStorage.getItem("allDeviceColumn");
    const existing: Record<string, boolean> = columnSetting ? JSON.parse(columnSetting) : {};

    // 沒有 localStorage → 全部預設 true，但不存回 localStorage
    if (!columnSetting) {
      statusOptions.forEach((key) => {
        existing[key] = true;
      });
    }

    // 設定當前 row 的狀態
    const savedList = rowOptions.filter((opt) => existing[opt]);
    setCheckedList(savedList);
    setCheckAll(savedList.length === rowOptions.length);
    setIndeterminate(!!savedList.length && savedList.length < rowOptions.length);

    setSelectedOptions(existing);
  }, []);

  const updateStorage = (list: string[]) => {
    const columnSetting = localStorage.getItem("allDeviceColumn");
    const existing: Record<string, boolean> = columnSetting ? JSON.parse(columnSetting) : {};

    // 確保所有 statusOptions 都存在，預設 true
    statusOptions.forEach((key) => {
      if (existing[key] === undefined) existing[key] = true;
    });

    // 更新當前這組的狀態
    rowOptions.forEach((opt) => {
      existing[opt] = list.includes(opt);
    });

    setCheckedList(list);
    setSelectedOptions(existing);
    setIndeterminate(!!list.length && list.length < rowOptions.length);
    setCheckAll(list.length === rowOptions.length);

    // ✅ 僅在第一次操作或已存在設定時才寫入
    if (columnSetting || list.length !== rowOptions.length) {
      localStorage.setItem("allDeviceColumn", JSON.stringify(existing));
    }
  };

  const onChange = (list: CheckboxValueType[]) => updateStorage(list.map(String));
  const onCheckAllChange = (e: CheckboxChangeEvent) => updateStorage(e.target.checked ? rowOptions : []);

  return (
    <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
      <Col>
        <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
          {t(`ISMS.${rowOptionKey}`)}
        </Checkbox>
      </Col>
      <Col>
        {rowOptions.length > 1 && (
          <Checkbox.Group
            value={checkedList}
            onChange={onChange}
            options={rowOptions.map((key) => ({
              label: t(`ISMS.${key}`),
              value: key,
            }))}
          />
        )}
      </Col>
    </Row>
  );
};

const DeviceTableC: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = screens.xs;
  const disp = useAppDispatch();
  const { stateCid, loginCid, level, time_zone } = useAppSelector((state) => state.global);
  const { deleteNode: deleteNodeAllow } = useAppSelector((state) => state.dashboard);

  const [form] = Form.useForm();
  const [phone_form] = Form.useForm();
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [editingKey, setEditingKey] = useState(undefined);
  const [editCoordinateKey, setEditCoordinateKey] = useState(undefined);
  const [phoneEditingKey, setPhoneEditingKey] = useState(undefined);
  const { data: deviceStatus, isFetching: deviceStatusFetching } = useDeviceStatus(level, stateCid, loginCid);

  const [deviceTableData, setDeviceTableData] = useState<Record<keyof TableData, any>[]>(
    deviceStatus?.response?.tableData || []
  );
  const { mutateAsync: rebootMutation } = useDeviceReboot();
  const { mutateAsync: deleteNodeMutation } = useDeleteNode(stateCid);
  const { mutateAsync: editCoordinateMutation } = useEditCoordinate(stateCid);
  const { mutateAsync: deviceRenameMutation } = useDviceRename(stateCid);
  const { data: customerInf, isLoading: customerInfLoading } = useCustomerInf(level, stateCid, loginCid);
  const { mutateAsync: updateStatusMutation } = useUpdateStatus(stateCid);
  const forbiddenLevel = level === "get" || (level === "super_super" && stateCid === "");
  const [showAll, setShowAll] = useState(false);
  /*250904 新增 Table Column 動態設定 
    可調整為[ “Location” , “Description” , “GPS” , “Phone Number” , “Last Update” , “Latest Connection Time” ]，
    若 Location 或 Description 隱藏 Filter 篩選欄位也需要動態移除
  */
  const [BtnVisible, setBtnVisible] = useState(false);
  // 這裡 value 不是 string[]，而是 boolean
  const [selectedOptions, setSelectedOptions] = useState<Record<string, boolean>>({});

  const columnSetting: columnSetting = JSON.parse(localStorage.getItem("allDeviceColumn") ?? "{}");
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // 當 tableData 或 columnSetting 變動時重置所有展開
  useEffect(() => {
    setExpandedRows(new Set());
  }, [deviceTableData.length, JSON.stringify(columnSetting)]);

  const content = (
    <div style={{ maxWidth: "1500px" }}>
      {statusOptions.map((key, index) => (
        <CheckDeviceRow
          key={index}
          rowOptionKey={key}
          rowOptions={[key]} // ✅ 單一選項陣列
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          t={t}
        />
      ))}
      <Divider style={{ margin: "12px 0" }} />
      <Button onClick={() => setBtnVisible(false)}>Close</Button>
    </div>
  );

  useEffect(() => {
    disp(setImportSource("NewConfig"));
  }, []);

  // customer map
  const customerMap: any = {};
  customerInf?.response?.map((item: any) => {
    customerMap[item.cid] = item.inf_list.company;
  });

  const edit = (index: any, column: string) => {
    if (column === "name") {
      setEditingKey(index);
    } else if (column === "phone_number") {
      setPhoneEditingKey(index);
    } else {
      setEditCoordinateKey(index);
    }
  };

  const cancel = (column: string) => {
    if (column === "name") {
      setEditingKey(undefined);
    } else if (column === "phone_number") {
      setPhoneEditingKey(undefined);
    } else {
      setEditCoordinateKey(undefined);
    }
  };

  const rebootDevices = (selectedRowKeys: string[]) => {
    //server only accept device ID
    // let deviceID = deviceNameToID(deviceTableData, selectedRowKeys);

    rebootMutation(selectedRowKeys)
      .then((res: any) => {
        disp(setResultModalVisible(true));
        disp(
          setResultList({
            failureDevices: [],
            rebootDevices: [],
            errorDevices: [],
            successDevices: [],
            ...res,
          })
        );
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  // FIXME:
  const editCoordinate = (value: any, record: any) => {
    editCoordinateMutation({ id: record.id, lati_longi: value.lati_longi });
    cancel("lati_longi");
  };

  const deviceRename = (value: any, record: any) => {
    //avoid user name a device with other device's ID
    const IdList = deviceStatus?.response?.devicesID;
    const is_Name_equal_to__ID = IdList?.includes(value.name) && value.name !== record.id;
    if (is_Name_equal_to__ID) {
      message.error(t("ISMS.Duplicated"));

      return;
    }

    deviceRenameMutation({ cid: record.cid, id: record.id, name: value.name });
    cancel("name");
  };

  const editPhoneNumber = (value: any, record: any) => {
    updateStatusMutation({ deviceID: record.id, phone_number: value.phone_number })
      .then(() => {
        message.success(t("ISMS.Success"));
      })
      .catch((error) => {
        message.error(t("ISMS.Fail"));
      });
    cancel("phone_number");
  };

  let paginationSetting: any;

  const SettingData = localStorage.getItem("setting.page");

  if (SettingData) {
    paginationSetting = JSON.parse(SettingData);
  }

  const [defaultPageSize, setDefaultPageSize] = useState(paginationSetting?.devicePageSize || 20);

  const OnPageChange = (_: any, size: number) => {
    setDefaultPageSize(size);
    if (paginationSetting?.devicePageSize) {
      paginationSetting.devicePageSize = size;
    } else {
      paginationSetting = { devicePageSize: size };
    }
    localStorage.setItem("setting.page", JSON.stringify(paginationSetting));
  };

  const deleteNode = (deleteList: string[]) => {
    if (deleteList.length === 0) {
      message.warning(t("ISMS.Connected cannot be Deleted"));
    } else {
      deleteNodeMutation({ level, stateCid, loginCid, deleteList })
        .then(() => message.success(t("ISMS.Success")))
        .catch(() => message.error(t("ISMS.Fail")));
    }
  };

  const showConfirm = () => {
    confirm({
      title: "Do you want to delete these devices?",
      icon: <ExclamationCircleOutlined />,
      content: "Will delete offline devices only",
      onOk() {
        //篩選出offline device(已連線不可刪除)
        const offlineDevices = deviceTableData
          .filter((item: any) => item.health === "offline")
          .map((item: any) => item.id);

        const deleteList = selectedRowKeys.filter((item) => {
          return offlineDevices.includes(item);
        });

        deleteNode(deleteNodeAllow ? selectedRowKeys : deleteList);

        setSelectedRowKeys([]);

        // deleteNodeMutation({ level, stateCid, loginCid, deleteList })
        //   .then(() => message.success(t("ISMS.Success")))
        //   .catch(() => message.error(t("ISMS.Fail")));
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="location"
        disabled={!selectedRowKeys.length || forbiddenLevel}
        onClick={() => {
          if (!selectedRowKeys?.length) {
            return;
          }
          disp(setEditCityVisible(true));
        }}
      >
        {t("ISMS.Edit Location")}
      </Menu.Item>
      <Menu.Item
        key="description"
        disabled={!selectedRowKeys.length || forbiddenLevel}
        onClick={() => {
          if (!selectedRowKeys?.length) {
            return;
          }
          disp(setEditDescriptionVisible(true));
        }}
      >
        {t("ISMS.Edit Description")}
      </Menu.Item>

      <Menu.Item
        key="reboot"
        disabled={!selectedRowKeys.length || forbiddenLevel}
        onClick={() => {
          if (!selectedRowKeys?.length) {
            return;
          }
          rebootDevices(selectedRowKeys);
          setSelectedRowKeys([]);
        }}
      >
        {t("ISMS.Reboot")}
      </Menu.Item>
      <Menu.Item
        key="delete"
        disabled={!selectedRowKeys.length || forbiddenLevel}
        onClick={() => {
          if (!selectedRowKeys?.length) {
            return;
          }

          showConfirm();
        }}
      >
        {t("ISMS.Delete")}
      </Menu.Item>
    </Menu>
  );

  return (
    <Fragment>
      <SettingResultModal />

      <EditCityModal selectedRowKeys={selectedRowKeys} setSelectedRowKeys={setSelectedRowKeys} />

      <EditDescriptionModal selectedRowKeys={selectedRowKeys} setSelectedRowKeys={setSelectedRowKeys} />

      <HealthModal />

      <DeviceStateModal />

      <IoTModal />

      <TrackMap />

      <Filter setDeviceTableData={setDeviceTableData} columnSetting={columnSetting} />
      {/* <BackTop visibilityHeight={100} /> */}
      <Card
        title={t("ISMS.DevicesOverview")}
        bodyStyle={isMobile ? { padding: "12px" } : {}}
        extra={
          <Space size={1}>
            <Refresh queriesArray={["status"]} />

            <AutoRefresh queriesArray={["status"]} />
            {!isMobile && (
              <Tooltip title={`${t("ISMS.Setting")}`}>
                <Popover
                  placement="left"
                  trigger="click"
                  onVisibleChange={(visible) => setBtnVisible(visible)}
                  content={content}
                  visible={BtnVisible}
                  // style={{ overflowY: "auto" }}
                >
                  <Button icon={<SettingOutlined />}></Button>
                </Popover>
              </Tooltip>
            )}

            {isMobile ? (
              <Button
                // // style={{ position: "fixed", bottom: 5, right: "100px", zIndex: 39 }}
                // className={styles.trigger}
                // type="text"
                icon={showAll ? <VerticalAlignMiddleOutlined /> : <ColumnHeightOutlined />}
                onClick={() => setShowAll((prev) => !prev)}
              ></Button>
            ) : (
              <Dropdown overlay={menu} placement="bottomRight">
                <Button>
                  Action <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        }
      >
        {isMobile ? (
          <List
            className="demo-loadmore-list"
            itemLayout="horizontal"
            dataSource={deviceTableData}
            renderItem={(item, index) => (
              <OverviewListItem
                item={item}
                index={index}
                deviceStatusFetching={deviceStatusFetching}
                showAll={showAll}
                deviceRename={deviceRename}
                editPhoneNumber={editPhoneNumber}
                forbiddenLevel={forbiddenLevel}
                rebootDevices={rebootDevices}
                deleteNode={deleteNode}
              />
            )}
          />
        ) : (
          <Table
            sticky
            className={styles.table}
            rowClassName={() => "editable-row"}
            scroll={{ x: !screens.xs ? "max-content" : 1500 }}
            // scroll={{ x: 1500 }}
            pagination={{
              onShowSizeChange: (_, size) => OnPageChange(_, size),
              defaultPageSize: defaultPageSize,
              showSizeChanger: true,
            }}
            loading={deviceStatusFetching}
            dataSource={deviceTableData}
            rowKey={(record) => record.id}
            rowSelection={{
              selectedRowKeys,
              onChange: (selectedRowKeys) => {
                setSelectedRowKeys(selectedRowKeys as string[]);
              },
              columnTitle: (
                <Checkbox
                  checked={Boolean(selectedRowKeys.length)}
                  onChange={() =>
                    setSelectedRowKeys((keys: string[]) =>
                      keys.length === deviceTableData.length ? [] : deviceTableData.map((r: any) => r.id)
                    )
                  }
                />
              ),
            }}
          >
            {level === "super_super" && stateCid === "" && (
              <Column
                // ellipsis
                title={<Fragment>{t("ISMS.Customer")}</Fragment>}
                dataIndex="cid"
                width={120}
                fixed="left"
                render={(cid, index: number) => {
                  if (level === "super_super" && stateCid === "") {
                    return customerMap[`${cid}`] || "";
                  } else {
                    return "";
                  }
                }}
              />
            )}

            {columnSetting && columnSetting.Location !== false && (
              <Column
                // ellipsis={true}
                title={<Fragment>{t("ISMS.Location")}</Fragment>}
                dataIndex="location"
                width={120}
                sorter={(a: any, b: any) => {
                  return a.location?.length || 0 - b.location?.length || 0;
                }}
                sortDirections={["descend", "ascend"]}
              />
            )}

            {columnSetting && columnSetting.Description !== false && (
              <Column
                title={t("ISMS.Description")}
                dataIndex="description"
                width={177}
                render={(_: string, record: TableData, index: number) => {
                  const isExpanded = expandedRows.has(index);
                  //  const isExpanded = expandedRows.has(index);
                  return (
                    <Space size={0} style={{ width: "100%" }} className={styles.descriptionCol}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%", // 固定跟 column 一樣寬
                        }}
                      >
                        {/* Tooltip 只作用在文字 */}
                        <Tooltip title={record.description}>
                          <Paragraph
                            ellipsis={!isExpanded ? { rows: 1, expandable: true, symbol: " " } : false}
                            style={
                              !isExpanded
                                ? {
                                    margin: 0,
                                    whiteSpace: "normal",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    flex: 1, // 文字佔滿剩餘空間
                                    overflowWrap: "anywhere",
                                  }
                                : {
                                    margin: 0,
                                    flex: 1, // 文字佔滿剩餘空間
                                    whiteSpace: "normal",

                                    overflowWrap: "anywhere",
                                  }
                            }
                          >
                            {record?.description}
                          </Paragraph>
                        </Tooltip>
                        {record?.description !== "" && record?.description && (
                          <Tooltip
                            destroyTooltipOnHide
                            title={isExpanded ? t("ISMS.less") : t("ISMS.more")}
                            key={`${index}-${JSON.stringify(isExpanded)}`}
                          >
                            <Button
                              type="text"
                              icon={
                                isExpanded ? (
                                  <UpOutlined style={{ fontSize: "14px" }} />
                                ) : (
                                  <DownOutlined style={{ fontSize: "14px" }} />
                                )
                              }
                              onClick={() => {
                                const newSet = new Set(expandedRows);
                                if (newSet.has(index)) newSet.delete(index);
                                else newSet.add(index);
                                setExpandedRows(newSet);
                              }}
                            ></Button>
                          </Tooltip>
                        )}
                      </div>
                    </Space>
                  );
                }}
              />
            )}

            {columnSetting && columnSetting.GPS !== false && (
              <Column
                title={"GPS"}
                dataIndex="lati_longi"
                width={120}
                render={(_: string, record: TableData, index: number) => {
                  if (editCoordinateKey !== index) {
                    return (
                      <Space>
                        <Tooltip>
                          {!record.lati_longi ||
                          record.lati_longi === "" ||
                          record.lati_longi[0] + record.lati_longi[1] === 0
                            ? ""
                            : record.lati_longi.join(",")}
                        </Tooltip>
                        <Tooltip title={forbiddenLevel ? WarningString : t("ISMS.edit")}>
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            disabled={forbiddenLevel}
                            onClick={() => {
                              form.setFieldsValue({
                                lati_longi:
                                  !record.lati_longi || record.lati_longi[0] + record.lati_longi[1] === 0
                                    ? ""
                                    : record.lati_longi,
                              });
                              edit(index, "lati_longi");
                            }}
                          ></Button>
                        </Tooltip>
                      </Space>
                    );
                  } else {
                    return (
                      <Form form={form} onFinish={(value) => editCoordinate(value, record)} layout="vertical">
                        <Row align="middle">
                          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                            <Form.Item name={"lati_longi"} rules={[CoordinateVerify]} style={{ marginBottom: 0 }}>
                              <Input placeholder="Coordinate" maxLength={30} />
                            </Form.Item>

                            <Space size={1}>
                              <Button
                                type="text"
                                onClick={() => {
                                  form.submit();
                                }}
                              >
                                {t("ISMS.Save")}
                              </Button>

                              <Button
                                type="text"
                                onClick={() => {
                                  cancel("lati_longi");
                                }}
                              >
                                {t("ISMS.Cancel")}
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Form>
                    );
                  }
                }}
              />
            )}

            <Column
              title={t("ISMS.Device")}
              dataIndex="name"
              width={100}
              render={(_: string, record: TableData, index: number) => {
                if (editingKey !== index) {
                  return (
                    // TODO: /Overview /Devices Overview  組件 Device 欄位：如果資訊只有中文時會自動換行問題 新增 CSS ：whiteSpace: "nowrap"
                    <Space style={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={record.id}>{record.name_or_id}</Tooltip>
                      <Tooltip title={forbiddenLevel ? WarningString : t("ISMS.rename")}>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          disabled={forbiddenLevel}
                          onClick={() => {
                            form.setFieldsValue({
                              name: record.name !== "" ? record.name : record.id,
                            });
                            edit(index, "name");
                          }}
                        ></Button>
                      </Tooltip>
                    </Space>
                  );
                } else {
                  return (
                    <Form form={form} onFinish={(value) => deviceRename(value, record)} layout="vertical">
                      <Row align="middle">
                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                          <Form.Item name={"name"} rules={[CharacterVerify]} style={{ marginBottom: 0 }}>
                            <Input placeholder="New Device Name" maxLength={15} />
                          </Form.Item>

                          <Space size={1}>
                            <Button
                              type="text"
                              onClick={() => {
                                form.submit();
                              }}
                            >
                              {t("ISMS.Save")}
                            </Button>

                            <Button
                              type="text"
                              onClick={() => {
                                cancel("name");
                              }}
                            >
                              {t("ISMS.Cancel")}
                            </Button>
                          </Space>
                        </Col>
                      </Row>
                    </Form>
                  );
                }
              }}
              // sorter={(a: any, b: any) => {
              //   console.log("sort", a.name_or_id?.length || 0 - b.name_or_id?.length || 0);
              //   return a.name_or_id?.length || 0 - b.name_or_id?.length || 0;
              // }}
              // sortDirections={["descend", "ascend"]}
            />

            <Column responsive={["sm"]} title={t("ISMS.Model")} dataIndex="model" width={100} />

            {columnSetting && columnSetting.PhoneNumber !== false && (
              <Column
                title={t("ISMS.Phone Number")}
                dataIndex="phone_number"
                // responsive={["lg"]}
                width={120}
                render={(_: string, record: TableData, index: number) => {
                  if (phoneEditingKey !== index) {
                    return (
                      <Space>
                        {record.phone_number_status !== ""
                          ? record.phone_number_status
                          : record.phone_number_nodeInf !== `0000000000000`
                          ? record.phone_number_nodeInf
                          : ""}

                        {record.phone_number_status === "" && (
                          <Tooltip title={forbiddenLevel ? WarningString : t("ISMS.edit")}>
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              disabled={forbiddenLevel}
                              onClick={() => {
                                phone_form.setFieldsValue({
                                  phone_number:
                                    record.phone_number_nodeInf === `0000000000000` ? "" : record.phone_number_nodeInf,
                                });
                                edit(index, "phone_number");
                              }}
                            ></Button>
                          </Tooltip>
                        )}
                      </Space>
                    );
                  } else {
                    return (
                      <Form form={phone_form} onFinish={(value) => editPhoneNumber(value, record)}>
                        <Row>
                          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                            <Form.Item
                              name={"phone_number"}
                              rules={[{ required: false, message: "required!!" }, NumberVerify]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="+886XXXXXXXXX or 0XXXXXXXXX" maxLength={20} />
                            </Form.Item>

                            <Space size={1}>
                              <Button type="text" onClick={() => phone_form.submit()}>
                                {t("ISMS.Save")}
                              </Button>

                              <Button type="text" onClick={() => cancel("phone_number")}>
                                {t("ISMS.Cancel")}
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </Form>
                    );
                  }
                }}
              />
            )}

            {columnSetting && columnSetting.LastUpdate !== false && (
              <Column
                title={t("ISMS.LastUpdate")}
                dataIndex="lastupdate"
                width={150}
                responsive={["sm"]}
                sorter={(a: any, b: any) => a.lastupdate - b.lastupdate}
                render={(text) => {
                  return TimestampToDate(text * 1000, time_zone);
                }}
              />
            )}
            {columnSetting && columnSetting.LatestConnectionTime !== false && (
              <Column
                title={t("ISMS.LatestConnectionTime")}
                dataIndex="latest_connection_time"
                // responsive={["sm"]}
                width={180}
                sorter={(a: any, b: any) => a.latest_connection_time - b.latest_connection_time}
                render={(text) => {
                  return ConnectionTime(text, t("ISMS.Day"), t("ISMS.Second"));
                }}
              />
            )}

            <Column
              title={t("ISMS.Info")}
              // TODO: /Overview /Devices Overview  組件 Info 欄位：新增寬度 200，使展開時不會寬度太大
              width={250}
              dataIndex="information"
              fixed="right"
              render={(_, record: TableData, index) => {
                return <Icons record={record} index={index} />;
              }}
            />
          </Table>
        )}
      </Card>
    </Fragment>
  );
};

export default DeviceTableC;

interface ListProps {
  item: any;
  index: number;
  deviceStatusFetching: boolean;
  showAll: boolean;
  forbiddenLevel: boolean;
  deviceRename: (value: any, record: any) => void;
  editPhoneNumber: (value: any, record: any) => void;
  rebootDevices: (arg: string[]) => void;
  deleteNode: (arg: string[]) => void;
}

const OverviewListItem = React.memo(
  ({
    item,
    index,
    deviceStatusFetching,
    showAll,
    deviceRename,
    editPhoneNumber,
    forbiddenLevel,
    rebootDevices,
    deleteNode,
  }: ListProps) => {
    const { t } = useTranslation();
    const { stateCid } = useAppSelector((state) => state.global);

    const [show, setShow] = useState(showAll);
    const { mutateAsync: updateLocationMutation, isLoading: updateLocationLoading } = useUpdateLocations(stateCid);
    const { mutateAsync: updateDescriptionMutation, isLoading: updateDescriptionLoading } =
      useUpdateDescriptions(stateCid);

    const updateLocationOnFinish = (value: any) => {
      updateLocationMutation({ value })
        .then(() => {
          message.success(t("ISMS.Success"));
        })
        .catch(() => {
          message.error(t("ISMS.Fail"));
        });
    };

    const updateDescriptionOnFinish = (value: any) => {
      updateDescriptionMutation({ value })
        .then(() => {
          message.success(t("ISMS.Success"));
        })
        .catch(() => {
          message.error(t("ISMS.Fail"));
        });
    };

    useEffect(() => {
      setShow(showAll);
    }, [showAll]);

    return (
      <List.Item
        className="relative"
        onClick={(e) => {
          e.stopPropagation();
          if ((e.target as HTMLElement).tagName !== "DIV") return;
          setShow((prev) => !prev);
        }}
      >
        <Skeleton avatar title={false} loading={deviceStatusFetching} active>
          <List.Item.Meta
            title={
              <div className="flex items-center justify-between">
                <Paragraph
                  className="my-0"
                  editable={
                    forbiddenLevel
                      ? false
                      : {
                          onChange: (name) => deviceRename({ name }, item),
                        }
                  }
                >
                  {item.name_or_id}
                </Paragraph>

                <Icons record={item} index={index} exclude={"others"} />
              </div>
            }
            description={
              show ? (
                <Fragment>
                  <Divider className="my-3" />
                  <Paragraph>model: {item.model}</Paragraph>
                  <Paragraph
                    // className="my-0"
                    editable={
                      forbiddenLevel
                        ? false
                        : {
                            maxLength: 30,
                            onChange: (value) => updateLocationOnFinish({ devices: [item.id], city_name: value }),
                          }
                    }
                  >
                    location: {item.city ?? "N/A"}
                  </Paragraph>
                  <Paragraph
                    // className="my-0"
                    editable={
                      forbiddenLevel
                        ? false
                        : {
                            maxLength: 100,
                            onChange: (value) =>
                              updateDescriptionOnFinish({ devices: [item.id], description_name: value }),
                          }
                    }
                  >
                    description: {item.description ?? "N/A"}
                  </Paragraph>
                  {item.phone_number_status === "" && (
                    <Paragraph
                      // className="my-0"
                      editable={
                        forbiddenLevel
                          ? false
                          : {
                              onChange: (phone_number) => editPhoneNumber({ phone_number }, item),
                            }
                      }
                    >
                      phone: {item.phone_number_status === "" ? item.phone_number_nodeInf : item.phone_number_status}
                    </Paragraph>
                  )}

                  <div className="flex justify-between">
                    <Icons record={item} index={index} exclude="state" />
                    <Space>
                      <Button
                        disabled={forbiddenLevel}
                        size="small"
                        type="text"
                        danger
                        onClick={() => rebootDevices([item.id])}
                      >
                        Reboot
                      </Button>
                      <Button
                        disabled={forbiddenLevel || item.health !== "offline"}
                        size="small"
                        type="text"
                        danger
                        onClick={() => deleteNode([item.id])}
                      >
                        Delete
                      </Button>
                    </Space>
                  </div>
                </Fragment>
              ) : null
            }
          />
        </Skeleton>
      </List.Item>
    );
  }
);
